// Intent: add a CUSTOM reminder by hand (owner: the "+" → Przypomnienie used to
// just open the list — nothing to add). Most reminders are auto-generated from
// policies/fixed costs; this covers the rest (a call, a document to renew, etc.).

import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { DateField } from '@/components/ui/date-field';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useRemindersStore } from '@/store/reminders-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { Reminder } from '@/types/domain';
import { uuid } from '@/utils/format';

export default function NewReminderScreen() {
  const { t } = useTranslation();
  const addReminder = useRemindersStore((s) => s.addReminder);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [dateIso, setDateIso] = useState<string | null>(null);

  const valid = title.trim().length > 0 && dateIso !== null;

  const onSave = async () => {
    if (!valid) return;
    const now = new Date().toISOString();
    const reminder: Reminder = {
      id: uuid(),
      policyId: null,
      type: 'CUSTOM',
      title: title.trim(),
      subtitle: note.trim() || null,
      eventDate: dateIso!,
      leadDaysBefore: [7, 1, 0],
      enabled: true,
      lastFiredAt: null,
      updatedAt: now,
    };
    await addReminder(reminder);
    router.back();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('newReminder.title')} />
        <AppText variant="bodyMd">{t('newReminder.subtitle')}</AppText>

        <TextField
          label={t('newReminder.nameLabel')}
          placeholder={t('newReminder.namePlaceholder')}
          value={title}
          onChangeText={setTitle}
        />
        <DateField label={t('newReminder.dateLabel')} value={dateIso} onChange={setDateIso} />
        <TextField
          label={t('newReminder.noteLabel')}
          placeholder={t('newReminder.notePlaceholder')}
          value={note}
          onChangeText={setNote}
        />

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, backgroundColor: colors.infoBg, borderRadius: radius.card, padding: spacing.md }}>
          <Ionicons name="notifications-outline" size={18} color={colors.primary} />
          <AppText variant="labelMd" color={colors.navy} style={{ flex: 1 }}>
            {t('newReminder.hint')}
          </AppText>
        </View>

        <PrimaryButton label={t('newReminder.save')} disabled={!valid} onPress={onSave} />
      </ScrollView>
    </SafeAreaView>
  );
}
